export const theme = {
  primary: '#d48806',
  primaryLight: '#fef9e8',
  shadow: 'rgba(212, 136, 6, 0.1)',
  gradient: 'linear-gradient(125deg, #3d2800 0%, #8a5a00 42%, #d48806 100%)',
  overlay: 'radial-gradient(ellipse 50% 60% at 85% 40%, rgba(255, 200, 80, 0.18) 0%, transparent 55%)'
}

export const pageMeta = {
  title: '智慧巡检管理系统',
  badge: 'AI赋能空地一体巡检',
  description:
    '融合固定摄像头、无人机巡航、AI视觉算法与广播驱离能力，对巡查区域进行7×24智能识别与预警，联动事件调度处置，打造"感知—识别—告警—驱离—调度—闭环"一体化智慧巡检平台。',
  stats: [
    { value: '摄像头', label: '固定监测' },
    { value: '无人机', label: '空中巡航' },
    { value: 'AI', label: '智能识别' },
    { value: '闭环', label: '调度处置' }
  ]
}

export const sections = {
  features: {
    title: '核心功能',
    desc: '六大功能模块覆盖智慧巡检全流程，以"摄像头+无人机+AI+广播"构建空地协同、自动预警、闭环处置的巡检体系。'
  },
  whyUs: {
    title: '为什么选择我们',
    desc: '深耕AI视觉与空地协同巡检领域，以算法能力与落地经验助力巡查监管提质增效。'
  },
  related: {
    title: '相关产品',
    desc: '探索更多小智科技政务数字化解决方案。'
  }
}

export const features = [
  {
    icon: '📹',
    title: 'AI视频智能识别',
    desc: '对接固定摄像头与高点监控，基于AI视觉算法实时识别入侵、烟火、非法采挖、违建等异常行为，自动抓拍留证并触发告警。'
  },
  {
    icon: '🚁',
    title: '无人机空中巡检',
    desc: '支持无人机航线规划、定时巡航与应急起飞，空中视角补盲死角，实时回传画面并联动AI识别，实现空地协同全覆盖巡查。'
  },
  {
    icon: '📢',
    title: '广播联动驱离',
    desc: '识别到违规行为后，自动联动区域广播设备播放预警语音，远程喊话驱离，第一时间制止违法，减少人工到场成本。'
  },
  {
    icon: '⚡',
    title: '事件调度处置',
    desc: '告警事件自动入库、分级分类、分派责任人员，支持限时处置、督办催办与结果反馈，形成完整业务闭环。'
  },
  {
    icon: '🔗',
    title: '多源设备统一接入',
    desc: '统一接入摄像头、无人机、广播喇叭、传感器等设备，兼容主流协议与品牌，一平台管理、一屏调度。'
  },
  {
    icon: '🖥️',
    title: '巡检指挥大屏',
    desc: '可视化呈现巡查区域、设备在线状态、实时画面、告警事件与处置进度，辅助指挥中心高效研判与调度。'
  }
]

export const architecture = {
  variant: 'inspection',
  title: '智慧巡检管理系统',
  desc: '以"展示—应用—平台—基础"四层架构为核心，融合AI算法引擎与联动调度平台，构建空地协同、智能识别、预警处置的智慧巡检解决方案。',
  layers: [
    {
      name: '展示层',
      items: [
        '综合管理后台',
        '视频聚合平台',
        '预警平台',
        '算法处理平台',
        'GIS指挥调度平台',
        '移动指挥平台'
      ]
    },
    {
      name: '应用层',
      items: [
        '人的智能监管',
        '车的智能监管',
        '物的智能监管',
        '环境的非正常因素监管'
      ]
    },
    {
      name: '平台层',
      groups: [
        {
          name: 'AI能力中心',
          subgroups: [
            {
              title: 'AI算法引擎',
              items: [
                '水位检测',
                '渣土车设备监测',
                '危化车辆监测',
                '污水排放监测',
                '游泳监测',
                '溺水监测',
                '非法垂钓检测',
                '航运人员超载检测',
                '船舶非法驶入检测',
                '航运救生衣穿戴检测',
                '非法放牧识别'
              ]
            },
            {
              title: '算法管理平台',
              items: [
                '视频分析',
                '人员检测',
                '图片分类',
                '图像处理',
                '文字识别',
                '图像搜索',
                '人脸识别',
                '物品检测'
              ]
            }
          ]
        },
        {
          name: '联动平台',
          items: [
            '视频监控',
            '预警调度平台',
            'GIS系统',
            '移动执法平台'
          ]
        }
      ]
    },
    {
      name: '基础层',
      sections: [
        {
          title: '监控设备',
          type: 'devices',
          items: ['球机', '枪机', '半球', '高空瞭望']
        },
        {
          title: '算法计算中心',
          items: [
            '服务器',
            '网络设备',
            '安全设备',
            '存储设备',
            '操作系统',
            '数据库'
          ]
        }
      ]
    }
  ],
  footer: 'AI识别 · 空地协同 · 等保测评 · 私有化部署'
}

export const whyUs = [
  {
    title: '空地协同',
    desc: '固定摄像头与无人机优势互补，地面守点、空中巡线，实现对重点区域的立体化、无死角巡查覆盖。'
  },
  {
    title: 'AI精准识别',
    desc: '自研及适配多种视觉AI算法，支持入侵检测、烟火识别、非法采挖、水域漂浮物等场景，识别准确率高、误报可控。'
  },
  {
    title: '主动驱离',
    desc: '识别即广播，远程喊话驱离，将事后处置前移至事中干预，有效降低违法成本与监管人力投入。'
  },
  {
    title: '闭环管理',
    desc: '从告警发现到调度处置全程线上留痕，超时预警、督办催办，确保每一起事件件件有着落。'
  }
]

export const scenarios = {
  label: '巡检落地场景',
  title: '让固定监控、空中巡航和广播驱离协同发力',
  desc: '系统定位为"智慧巡检助手"，7×24自动识别巡查区域内的异常行为，联动广播驱离与人工调度，但最终处置决策仍由管理人员把关。',
  items: [
    {
      title: '矿区林区监管',
      desc: '摄像头守口、无人机巡山，AI识别非法采挖、盗伐偷运等行为，广播即时驱离，事件自动推送值班人员处置。'
    },
    {
      title: '河道水域巡查',
      desc: '高点监控与无人机巡航结合，识别非法捕捞、岸线侵占、漂浮物堆积等问题，告警联动执法调度。'
    },
    {
      title: '禁烧禁放监测',
      desc: 'AI识别烟火、焚烧等行为，广播预警驱离，同时推送网格员或执法人员到场核查，防范安全隐患。'
    },
    {
      title: '违建抢建预警',
      desc: '定时巡航比对影像变化，发现新增违建或抢建行为自动告警，留存影像证据并启动处置流程。'
    }
  ],
  highlights: [
    { title: '识别即响应', desc: 'AI识别异常后秒级触发广播驱离与事件推送，缩短从发现到响应的时间窗口。' },
    { title: '算法可配置', desc: '可按巡查区域配置识别类型、灵敏度、告警规则与广播话术，灵活适配不同场景。' },
    { title: '处置可追溯', desc: '告警、驱离、调度、处置全流程留痕，支持回放查证与绩效考核。' }
  ]
}

export const relatedProducts = [
  {
    icon: '🛡️',
    title: '综合治理',
    desc: '网格化管理、事件处置、矛盾调解、指挥调度一体化平台，与智慧巡检事件闭环无缝衔接。',
    route: '/comprehensive-governance',
    color: '#531dab'
  },
  {
    icon: '🌾',
    title: '数字乡村',
    desc: '乡村治理、智慧农业、便民服务、产业振兴全流程数字化，助力乡村振兴战略落地见效。',
    route: '/digital-village',
    color: '#0a6e4e'
  },
  {
    icon: '👔',
    title: '干部管理',
    desc: '基于AI的干部全生命周期管理平台，涵盖选拔、考核、培养、监督等环节。',
    route: '/cadre-management',
    color: '#0052d9'
  }
]
